import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';

interface CountryCode {
  value: string;
  label: string;
  dialCode: string;
}

interface PhoneInputProps {
  label?: string;
  name: string;
  value?: string;
  onChange: (value: string, countryCode?: string) => void;
  errors?: any;
  placeholder?: string;
  className?: string;
  labelClassName?: string;
  containerClass?: string;
}

const countryCodes: CountryCode[] = [
  { value: '+1', label: '+1', dialCode: '+1' },
  { value: '+44', label: '+44', dialCode: '+44' },
  { value: '+91', label: '+91', dialCode: '+91' },
  { value: '+61', label: '+61', dialCode: '+61' },
  { value: '+86', label: '+86', dialCode: '+86' },
  { value: '+81', label: '+81', dialCode: '+81' },
  { value: '+49', label: '+49', dialCode: '+49' },
  { value: '+33', label: '+33', dialCode: '+33' },
  { value: '+39', label: '+39', dialCode: '+39' },
  { value: '+34', label: '+34', dialCode: '+34' },
  { value: '+31', label: '+31', dialCode: '+31' },
  { value: '+46', label: '+46', dialCode: '+46' },
  { value: '+47', label: '+47', dialCode: '+47' },
  { value: '+45', label: '+45', dialCode: '+45' },
  { value: '+358', label: '+358', dialCode: '+358' },
  { value: '+48', label: '+48', dialCode: '+48' },
  { value: '+420', label: '+420', dialCode: '+420' },
  { value: '+36', label: '+36', dialCode: '+36' },
  { value: '+43', label: '+43', dialCode: '+43' },
  { value: '+41', label: '+41', dialCode: '+41' },
  { value: '+32', label: '+32', dialCode: '+32' },
  { value: '+351', label: '+351', dialCode: '+351' },
  { value: '+30', label: '+30', dialCode: '+30' },
  { value: '+90', label: '+90', dialCode: '+90' },
  { value: '+7', label: '+7', dialCode: '+7' },
  { value: '+380', label: '+380', dialCode: '+380' },
];

const PhoneInput: React.FC<PhoneInputProps> = ({
  label,
  name,
  value = '',
  onChange,
  errors,
  className = '',
  labelClassName = '',
  containerClass = '',
}) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(countryCodes[0]);
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    if (value) {
      // Parse existing value to extract country code and phone number
      const countryCode = countryCodes.find(code => value.startsWith(code.dialCode));
      if (countryCode) {
        setSelectedCountry(countryCode);
        const phoneNumber = value.replace(countryCode.dialCode, '');
        setDisplayValue(formatPhoneNumber(phoneNumber));
      } else {
        setDisplayValue(formatPhoneNumber(value));
      }
    }
  }, [value]);

  const formatPhoneNumber = (input: string): string => {
    // Remove all non-digits
    const digits = input.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handleCountryChange = (selected: any) => {
    setSelectedCountry(selected);
    // Re-format the current phone number with new country code
    const digits = displayValue.replace(/\D/g, '');
    const fullNumber = selected.dialCode + digits;
    onChange(fullNumber, selected.dialCode);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // Allow backspace and delete
    if (input.length < displayValue.length) {
      setDisplayValue(input);
      const digits = input.replace(/\D/g, '');
      const fullNumber = selectedCountry.dialCode + digits;
      onChange(fullNumber, selectedCountry.dialCode);
      return;
    }

    // Format the input
    const formatted = formatPhoneNumber(input);
    setDisplayValue(formatted);

    // Extract digits and send full number
    const digits = formatted.replace(/\D/g, '');
    const fullNumber = selectedCountry.dialCode + digits;
    onChange(fullNumber, selectedCountry.dialCode);
  };

  const formatOptionLabel = (option: CountryCode) => (
    <span>{option.label}</span>
  );

  return (
    <Form.Group className={containerClass}>
      {label && (
        <Form.Label className={labelClassName}>
          {label}
        </Form.Label>
      )}
      <div className="phone-input-container">
        <div className="country-select-wrapper">
          <Select
            value={selectedCountry}
            onChange={handleCountryChange}
            options={countryCodes}
            formatOptionLabel={formatOptionLabel}
            className="phone-country-select"
            classNamePrefix="select"
            isSearchable
            placeholder="Code"
            menuPlacement="auto"
          />
        </div>
        <div className="phone-input-wrapper">
          <Form.Control
            type="tel"
            value={displayValue}
            onChange={handlePhoneChange}
            placeholder="(555) 123-4567"
            className={`phone-number-input ${className}`}
            isInvalid={errors && errors[name]}
            maxLength={14}
          />
          {errors && errors[name] && (
            <Form.Control.Feedback type="invalid">
              {errors[name]?.message}
            </Form.Control.Feedback>
          )}
        </div>
      </div>
    </Form.Group>
  );
};

export default PhoneInput;
